import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Circle, Search, Target, X } from "lucide-react";
import { clsx } from "clsx";
import { searchFlow, type SearchResult } from "../lib/searchCore";
import { useGoalStore } from "../stores/goalStore";
import { useProjectStore } from "../stores/projectStore";
import { useTaskStore } from "../stores/taskStore";
import { useUiStore } from "../stores/uiStore";

export function GlobalSearchButton({ className }: { className?: string }) {
  const setSearchOpen = useUiStore((s) => s.setSearchOpen);
  return (
    <button
      type="button"
      aria-label="検索"
      title="検索"
      onClick={() => setSearchOpen(true)}
      className={clsx(
        "flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted hover:bg-hover dark:border-border-dark dark:text-muted-dark dark:hover:bg-hover-dark",
        className
      )}
    >
      <Search className="h-4 w-4" />
    </button>
  );
}

export function GlobalSearchPanel() {
  const open = useUiStore((s) => s.searchOpen);
  const setOpen = useUiStore((s) => s.setSearchOpen);
  const setAppView = useUiStore((s) => s.setAppView);
  const setEditorOpen = useUiStore((s) => s.setEditorOpen);
  const setDomain = useUiStore((s) => s.setDomain);
  const goals = useGoalStore((s) => s.goals);
  const goalsLoaded = useGoalStore((s) => s.loaded);
  const loadGoals = useGoalStore((s) => s.load);
  const setGoalFocus = useGoalStore((s) => s.setFocus);
  const tasks = useTaskStore((s) => s.allTasks);
  const projects = useProjectStore((s) => s.projects);
  const projectsLoaded = useProjectStore((s) => s.loaded);
  const loadProjects = useProjectStore((s) => s.load);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (!goalsLoaded) void loadGoals();
    if (!projectsLoaded) void loadProjects();
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [goalsLoaded, loadGoals, loadProjects, open, projectsLoaded]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const results = useMemo(() => searchFlow(query, goals, tasks), [goals, query, tasks]);

  const close = () => setOpen(false);
  const openResult = (result: SearchResult) => {
    if (result.kind === "goal") {
      setGoalFocus(result.id);
      setAppView("goals");
    } else {
      const project = result.projectId ? projects.find((p) => p.id === result.projectId) ?? null : null;
      setDomain(project?.kind ?? "all");
      setAppView("lanes");
      setEditorOpen(true, result.id);
    }
    close();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 px-3 pt-16"
      role="dialog"
      aria-modal="true"
      aria-label="検索"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="w-full max-w-xl rounded-lg border border-border bg-card shadow-xl dark:border-border-dark dark:bg-card-dark">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2 dark:border-border-dark">
          <Search className="h-4 w-4 shrink-0 text-muted dark:text-muted-dark" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape" && !e.nativeEvent.isComposing) close();
              if (e.key === "Enter" && !e.nativeEvent.isComposing && results[0]) openResult(results[0]);
            }}
            placeholder="ゴール・タスクを検索"
            className="h-10 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-dim"
          />
          <button
            type="button"
            aria-label="閉じる"
            onClick={close}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-hover dark:text-muted-dark dark:hover:bg-hover-dark"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flow-scrollbar max-h-[60vh] overflow-y-auto p-2">
          {query.trim() && results.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted dark:text-muted-dark">該当なし</p>
          ) : !query.trim() ? (
            <p className="px-3 py-8 text-center text-sm text-muted dark:text-muted-dark">入力すると絞り込みます</p>
          ) : (
            <div className="space-y-1">
              {results.map((result) => (
                <button
                  key={`${result.kind}:${result.id}`}
                  type="button"
                  onClick={() => openResult(result)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-hover dark:hover:bg-hover-dark"
                >
                  <span
                    className={clsx(
                      "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                      result.kind === "goal"
                        ? "bg-brand-500/10 text-brand-700 dark:text-brand-200"
                        : "bg-bg text-muted dark:bg-bg-dark dark:text-muted-dark"
                    )}
                  >
                    {result.kind === "goal" ? (
                      <Target className="h-4 w-4" />
                    ) : result.completed ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <Circle className="h-4 w-4" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{result.title}</span>
                    <span className="block truncate text-xs text-muted dark:text-muted-dark">{result.subtitle}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
